// Fase 10 — Terminal de mostrador (POS).
//
// A diferencia de Pos.tsx (la vista de back-office: historial de turnos,
// auditoría de ventas, apertura/cierre de caja), esta pantalla es el "frame"
// dedicado que usa el cajero durante una venta real: selección de productos,
// identificación/alta rápida del cliente, cobro y comprobante — sin la barra
// lateral ni el resto de navegación del Shell administrativo, igual que una
// caja registradora de software real. Toda venta que se cobra aquí llama al
// mismo endpoint POST /pos/ventas que ya usa Pos.tsx, así que el impacto en
// inventario, contabilidad y el turno de caja es exactamente el mismo —
// en tiempo real, no hay un motor "aparte" para el mostrador.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "../router";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError, descargarArchivo } from "../api";
import { Icon } from "../icons";
import { money, MEDIO_PAGO_LABEL } from "./Pos";

type Turno = {
  id: number; caja: string; estado: string; fechaApertura: string; fechaCierre: string | null;
  baseInicial: number; totalEfectivo: number; totalTarjeta: number; totalTransferencia: number;
  usuarioAperturaNombre?: string | null;
};
type ProductoDisponible = {
  id: number; producto: string; cantidadDisponible: number; bodegaNombre: string; precioSugerido: number | null;
  unidadVenta: "unidad" | "peso"; codigoBarras: string | null;
};
type ClienteRapido = { id: number; nombre: string; nit: string | null; email: string | null };
// `cantidad` sigue siendo la unidad que ya entendía el backend: unidades
// enteras para productos "unidad", kilogramos para productos "peso" (mismo
// convenio implícito que ya usa todo el inventario de producto terminado).
// Para un producto "peso" el cajero trabaja en gramos en la pantalla — más
// natural para vender pescado al detal — así que la fuente de verdad ahí es
// `pesoGramos`, y `cantidad` se deriva dividiendo entre 1000 antes de armar
// el carrito y de enviar la venta; el backend nunca se entera de que existen
// los gramos.
type ItemCarrito = {
  inventarioPtId: number; producto: string; cantidadDisponible: number; cantidad: number; precio: number;
  unidadVenta: "unidad" | "peso"; pesoGramos?: number;
};
type Recibo = {
  id: number; numero: string; clienteNombre: string; base: number; valorTotal: number; netoRecibido: number;
  medioPago: string;
  // unidadVenta/pesoGramos se completan desde el carrito local (no vienen de
  // la respuesta del backend, que no conoce esa distinción) para poder
  // mostrar "500 g" en vez de "0.5" en el recibo y en la tirilla impresa.
  items: { producto: string; cantidad: number; precio: number; unidadVenta?: "unidad" | "peso"; pesoGramos?: number }[];
  correoEnviado: boolean; correoEstado: string; correoDestino: string | null; cude: string;
};

export function PosMostrador() {
  const { sesion, tienePermiso, logout } = useAuth();
  const puedeVender = tienePermiso("pos:vender");
  const puedeCaja = tienePermiso("pos:caja");

  const [turno, setTurno] = useState<Turno | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const cargarTurno = async () => {
    try {
      const t = await api.get("/pos/turnos/actual");
      setTurno(t);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar el turno de caja.");
    }
  };
  useEffect(() => { cargarTurno(); }, []);

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="bg-gradient-to-r from-navy to-navy-light text-white px-5 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/pos" className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <Icon name="arrowLeft" className="h-4.5 w-4.5" />
          </Link>
          <div className="h-9 w-9 rounded-xl bg-accent flex items-center justify-center font-display font-bold text-sm shrink-0">SG</div>
          <div>
            <h1 className="font-display text-sm font-bold leading-tight">Mostrador — Punto de venta</h1>
            <p className="text-[11px] text-white/60 leading-tight">{sesion?.empresa?.nombre}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {turno && (
            <div className="hidden sm:flex items-center gap-2 rounded-lg bg-white/10 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="text-xs font-medium">{turno.caja}</span>
              <span className="text-[11px] text-white/60">· abierto {turno.fechaApertura}</span>
            </div>
          )}
          <span className="text-xs text-white/70 hidden md:inline">{sesion?.usuario.nombre}</span>
          <button onClick={logout} className="h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" title="Cerrar sesión">
            <Icon name="logout" className="h-4 w-4" />
          </button>
        </div>
      </header>

      {error && <div className="mx-4 mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

      {turno === undefined && (
        <div className="flex-1 flex items-center justify-center text-sm text-textmuted">Cargando...</div>
      )}

      {turno === null && (
        <div className="flex-1 flex items-center justify-center px-4">
          {puedeCaja ? (
            <AperturaTurno onListo={() => cargarTurno()} />
          ) : (
            <div className="card p-6 max-w-sm text-center">
              <div className="h-11 w-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-3">
                <Icon name="creditCard" className="h-5 w-5" />
              </div>
              <p className="text-sm text-slate-700 font-semibold mb-1">No hay un turno de caja abierto</p>
              <p className="text-xs text-textmuted">Pídale a un supervisor con permiso de caja que abra el turno para poder vender.</p>
            </div>
          )}
        </div>
      )}

      {turno && puedeVender && <Terminal turno={turno} onTurnoActualizado={cargarTurno} empresaNombre={sesion?.empresa?.nombre || ""} vendedorNombre={sesion?.usuario.nombre || ""} />}

      {turno && !puedeVender && (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="card p-6 max-w-sm text-center">
            <p className="text-sm text-slate-700 font-semibold mb-1">Sin permiso para vender</p>
            <p className="text-xs text-textmuted">Su usuario puede ver el turno pero no tiene el permiso pos:vender.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- abrir turno (inline, no modal)
function AperturaTurno({ onListo }: { onListo: () => void }) {
  const { sesion } = useAuth();
  const primerNombre = (sesion?.usuario.nombre || "").split(" ")[0];
  const [caja, setCaja] = useState(primerNombre ? `Caja - ${primerNombre}` : "Caja mostrador");
  const [baseInicial, setBaseInicial] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const abrir = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/pos/turnos/abrir", { caja, baseInicial });
      onListo();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo abrir el turno.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={abrir} className="card w-full max-w-sm p-6 space-y-4">
      <div className="text-center mb-1">
        <div className="h-11 w-11 rounded-xl bg-accent-light text-accent-dark flex items-center justify-center mx-auto mb-3">
          <Icon name="banknote" className="h-5 w-5" />
        </div>
        <h3 className="text-base font-semibold text-slate-800">Abrir turno de caja</h3>
        <p className="text-xs text-textmuted mt-1">
          Antes de vender, abra su propio turno con la base inicial de efectivo. Cada cajero opera su caja por
          separado — el nombre que le dé aquí es el que verá el supervisor en el back office.
        </p>
      </div>
      <div><label className="block text-xs font-semibold text-slate-600 mb-1">Caja</label>
        <input className="input-field" value={caja} onChange={(e) => setCaja(e.target.value)} required /></div>
      <div><label className="block text-xs font-semibold text-slate-600 mb-1">Base inicial</label>
        <input className="input-field" type="number" min={0} step="1000" value={baseInicial} onChange={(e) => setBaseInicial(Number(e.target.value))} /></div>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      <button type="submit" className="btn-primary w-full" disabled={enviando}>{enviando ? "Abriendo..." : "Abrir turno y empezar a vender"}</button>
    </form>
  );
}

// ------------------------------------------------------------- terminal (venta activa)
function Terminal({ turno, onTurnoActualizado, empresaNombre, vendedorNombre }: { turno: Turno; onTurnoActualizado: () => void; empresaNombre: string; vendedorNombre: string }) {
  const [productos, setProductos] = useState<ProductoDisponible[]>([]);
  const [clientes, setClientes] = useState<ClienteRapido[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [carrito, setCarrito] = useState<ItemCarrito[]>([]);
  const [clienteId, setClienteId] = useState<number | "">("");
  const [medioPago, setMedioPago] = useState("efectivo");
  const [efectivoRecibido, setEfectivoRecibido] = useState<number | "">("");
  const [correoEnvio, setCorreoEnvio] = useState("");
  const [modalCliente, setModalCliente] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cobrando, setCobrando] = useState(false);
  const [recibo, setRecibo] = useState<Recibo | null>(null);
  // Defensa contra "Turno de caja no encontrado": el turno se carga una sola
  // vez al montar PosMostrador, pero puede cerrarse mientras el cajero sigue
  // armando el carrito (otra pestaña, un supervisor, etc.). Sin esto, el
  // cajero llegaba a un carrito completo y solo se enteraba al chocar contra
  // el backend en el botón Cobrar, con un error sin salida. Aquí se revalida
  // el turno periódicamente y otra vez justo antes de cobrar, para que el
  // cajero nunca llegue a ese punto muerto: si el turno ya no es válido, se
  // bloquea el carrito con una salida clara ("Abrir turno nuevo") en vez de
  // dejarlo frente a un banner rojo sin remedio.
  const [turnoVigente, setTurnoVigente] = useState(true);

  const turnoSigueAbierto = async () => {
    try {
      const actual = await api.get("/pos/turnos/actual");
      return !!actual && actual.id === turno.id && actual.estado === "abierto";
    } catch {
      // si la verificación falla por red, no se bloquea al cajero solo por
      // eso — el backend igual vuelve a validar el turno al cobrar.
      return true;
    }
  };

  useEffect(() => {
    const intervalo = setInterval(async () => {
      if (!(await turnoSigueAbierto())) {
        setTurnoVigente(false);
        clearInterval(intervalo);
      }
    }, 25000);
    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turno.id]);

  const cargarCatalogos = async () => {
    const [p, c] = await Promise.all([api.get("/pos/productos-disponibles"), api.get("/pos/clientes")]);
    setProductos(p);
    setClientes(c);
  };
  useEffect(() => { cargarCatalogos(); }, []);

  const productosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter((p) => p.producto.toLowerCase().includes(q));
  }, [productos, busqueda]);

  // Incremento por clic para un producto que se vende por peso: no tiene
  // sentido "+1" como con las unidades, así que cada clic en la tarjeta (o
  // cada escaneo repetido de su código de barras) suma 100g, un punto de
  // partida razonable que el cajero después ajusta a mano al peso real.
  const INCREMENTO_GRAMOS = 100;

  const agregarAlCarrito = (p: ProductoDisponible) => {
    setCarrito((prev) => {
      const existente = prev.find((it) => it.inventarioPtId === p.id);
      if (p.unidadVenta === "peso") {
        const disponibleGramos = Math.round(p.cantidadDisponible * 1000);
        if (existente) {
          const nuevoGramos = Math.min(disponibleGramos, (existente.pesoGramos || 0) + INCREMENTO_GRAMOS);
          if (nuevoGramos === existente.pesoGramos) return prev;
          return prev.map((it) => (it.inventarioPtId === p.id ? { ...it, pesoGramos: nuevoGramos, cantidad: nuevoGramos / 1000 } : it));
        }
        const gramosIniciales = Math.min(disponibleGramos, INCREMENTO_GRAMOS);
        if (gramosIniciales <= 0) return prev;
        return [...prev, {
          inventarioPtId: p.id, producto: p.producto, cantidadDisponible: p.cantidadDisponible,
          cantidad: gramosIniciales / 1000, pesoGramos: gramosIniciales, precio: p.precioSugerido || 0, unidadVenta: "peso",
        }];
      }
      if (existente) {
        if (existente.cantidad >= p.cantidadDisponible) return prev;
        return prev.map((it) => (it.inventarioPtId === p.id ? { ...it, cantidad: it.cantidad + 1 } : it));
      }
      return [...prev, {
        inventarioPtId: p.id, producto: p.producto, cantidadDisponible: p.cantidadDisponible,
        cantidad: 1, precio: p.precioSugerido || 0, unidadVenta: "unidad",
      }];
    });
  };

  const cambiarCantidad = (id: number, delta: number) => {
    setCarrito((prev) => prev.map((it) => {
      if (it.inventarioPtId !== id) return it;
      const nueva = Math.max(1, Math.min(it.cantidadDisponible, it.cantidad + delta));
      return { ...it, cantidad: nueva };
    }));
  };
  const cambiarPeso = (id: number, gramos: number) => {
    setCarrito((prev) => prev.map((it) => {
      if (it.inventarioPtId !== id) return it;
      const disponibleGramos = Math.round(it.cantidadDisponible * 1000);
      const nuevoGramos = Math.max(0, Math.min(disponibleGramos, Math.round(gramos)));
      return { ...it, pesoGramos: nuevoGramos, cantidad: nuevoGramos / 1000 };
    }));
  };
  const cambiarPrecio = (id: number, precio: number) => {
    setCarrito((prev) => prev.map((it) => (it.inventarioPtId === id ? { ...it, precio: Math.max(0, precio) } : it)));
  };
  const quitarDelCarrito = (id: number) => setCarrito((prev) => prev.filter((it) => it.inventarioPtId !== id));

  // ------------------------------------------------------- código de barras
  const [codigoEscaneado, setCodigoEscaneado] = useState("");
  const [errorEscaneo, setErrorEscaneo] = useState<string | null>(null);

  const procesarCodigoEscaneado = (codigo: string) => {
    const c = codigo.trim();
    if (!c) return;
    const producto = productos.find((p) => p.codigoBarras === c);
    if (!producto) {
      setErrorEscaneo(`Ningún producto tiene el código ${c}.`);
      setCodigoEscaneado("");
      return;
    }
    if (producto.cantidadDisponible <= 0) {
      setErrorEscaneo(`"${producto.producto}" no tiene existencia disponible.`);
      setCodigoEscaneado("");
      return;
    }
    agregarAlCarrito(producto);
    setErrorEscaneo(null);
    setCodigoEscaneado("");
  };

  const subtotal = carrito.reduce((acc, it) => acc + it.cantidad * it.precio, 0);
  const clienteSeleccionado = clientes.find((c) => c.id === clienteId);

  const seleccionarCliente = (id: number | "") => {
    setClienteId(id);
    const c = clientes.find((cl) => cl.id === id);
    setCorreoEnvio(c?.email || "");
  };

  const cobrar = async () => {
    if (carrito.length === 0) {
      setError("Agregue al menos un producto al carrito.");
      return;
    }
    setCobrando(true);
    setError(null);
    // Revalidación de último momento: aunque el sondeo periódico ya debería
    // haber detectado un turno cerrado, se vuelve a comprobar justo antes de
    // enviar la venta para cerrar cualquier carrera entre el intervalo y el
    // clic en "Cobrar".
    if (!(await turnoSigueAbierto())) {
      setTurnoVigente(false);
      setCobrando(false);
      return;
    }
    try {
      const resultado = await api.post("/pos/ventas", {
        turnoCajaId: turno.id,
        clienteId: clienteId || null,
        medioPago,
        items: carrito.map((it) => ({ inventarioPtId: it.inventarioPtId, cantidad: it.cantidad, precio: it.precio })),
        correo: correoEnvio || null,
      });
      // El backend devuelve los ítems en el mismo orden en que se enviaron
      // (los arma iterando el mismo arreglo), así que se pueden emparejar por
      // posición con el carrito local para heredar unidadVenta/pesoGramos —
      // datos que el backend no conoce (para él todo es "cantidad").
      const itemsConUnidad = (resultado.items || []).map((it: any, i: number) => ({
        ...it, unidadVenta: carrito[i]?.unidadVenta, pesoGramos: carrito[i]?.pesoGramos,
      }));
      setRecibo({
        id: resultado.id, numero: resultado.numero, clienteNombre: resultado.clienteNombre || "Consumidor final",
        base: resultado.base, valorTotal: resultado.valorTotal, netoRecibido: resultado.netoRecibido,
        medioPago: resultado.medioPago, items: itemsConUnidad,
        correoEnviado: !!resultado.correoEnviado, correoEstado: resultado.correoEstado, correoDestino: resultado.correoDestino,
        cude: resultado.cudeDocumentoEquivalente,
      });
      onTurnoActualizado();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "No se pudo registrar la venta.";
      // Última red de seguridad: si a pesar de las validaciones anteriores el
      // backend igual rechaza la venta por el turno (p. ej. otro supervisor
      // lo cerró en el instante entre la revalidación y el envío), se trata
      // igual que un turno vencido en vez de dejar el banner rojo genérico.
      if (/turno/i.test(msg)) {
        setTurnoVigente(false);
      } else {
        setError(msg);
      }
    } finally {
      setCobrando(false);
    }
  };

  const nuevaVenta = () => {
    setRecibo(null);
    setCarrito([]);
    setClienteId("");
    setMedioPago("efectivo");
    setEfectivoRecibido("");
    setCorreoEnvio("");
    setError(null);
    setCodigoEscaneado("");
    setErrorEscaneo(null);
    cargarCatalogos();
  };

  if (!turnoVigente) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="card p-6 max-w-sm text-center">
          <div className="h-11 w-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-3">
            <Icon name="creditCard" className="h-5 w-5" />
          </div>
          <p className="text-sm text-slate-700 font-semibold mb-1">Su turno de caja ya no está abierto</p>
          <p className="text-xs text-textmuted mb-4">
            {carrito.length > 0
              ? "Se cerró mientras armaba esta venta, así que no se puede cobrar. Abra un turno nuevo para seguir vendiendo — el carrito actual no se conserva."
              : "Debe abrir un turno nuevo antes de seguir vendiendo."}
          </p>
          <button type="button" className="btn-primary w-full" onClick={onTurnoActualizado}>Abrir turno nuevo</button>
        </div>
      </div>
    );
  }

  if (recibo) {
    return (
      <PantallaRecibo
        recibo={recibo}
        efectivoRecibido={efectivoRecibido}
        onNuevaVenta={nuevaVenta}
        empresaNombre={empresaNombre}
        vendedorNombre={vendedorNombre}
        cajaNombre={turno.caja}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
      {/* Panel de productos */}
      <div className="flex-1 flex flex-col p-4 min-w-0">
        <div className="relative mb-2 shrink-0">
          <Icon name="barcode" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-textmuted" />
          <input
            className={`input-field pl-9 ${errorEscaneo ? "border-red-300 focus:border-red-400 focus:ring-red-100" : ""}`}
            placeholder="Escanear código de barras (EAN-13)..."
            inputMode="numeric"
            value={codigoEscaneado}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "");
              setCodigoEscaneado(v);
              setErrorEscaneo(null);
              // La mayoría de lectores de código de barras USB/Bluetooth se
              // comportan como un teclado que escribe muy rápido y termina
              // con Enter — eso ya dispara el onKeyDown de abajo. Este envío
              // automático al llegar a 13 dígitos es una red adicional para
              // lectores que no mandan Enter (o para tipear el código a mano
              // sin tener que pulsar Enter, en pruebas sin lector físico).
              if (v.length === 13) procesarCodigoEscaneado(v);
            }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); procesarCodigoEscaneado(codigoEscaneado); } }}
          />
        </div>
        {errorEscaneo && <p className="text-[11px] text-red-600 mb-2 -mt-1">{errorEscaneo}</p>}
        <div className="relative mb-3 shrink-0">
          <Icon name="search" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-textmuted" />
          <input
            className="input-field pl-9"
            placeholder="Buscar producto por nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 content-start pb-4">
          {productosFiltrados.map((p) => {
            const enCarrito = carrito.find((it) => it.inventarioPtId === p.id);
            const agotado = enCarrito ? enCarrito.cantidad >= p.cantidadDisponible : p.cantidadDisponible <= 0;
            const porPeso = p.unidadVenta === "peso";
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => agregarAlCarrito(p)}
                disabled={agotado}
                className={`card p-3 text-left transition-all hover:shadow-card-hover hover:border-accent/30 disabled:opacity-40 disabled:cursor-not-allowed ${enCarrito ? "border-accent ring-1 ring-accent/25" : ""}`}
              >
                <div className="h-8 w-8 rounded-lg bg-accent-light text-accent-dark flex items-center justify-center mb-2">
                  <Icon name="box" className="h-4 w-4" />
                </div>
                <div className="text-xs font-semibold text-slate-800 leading-tight line-clamp-2">{p.producto}</div>
                <div className="text-[10px] text-textmuted mt-1">
                  {p.bodegaNombre} · {porPeso ? `${Math.round(p.cantidadDisponible * 1000)} g disp.` : `${p.cantidadDisponible} disp.`}
                </div>
                <div className="text-sm font-bold text-slate-800 mt-1.5">
                  {p.precioSugerido ? `${money(p.precioSugerido)}${porPeso ? "/kg" : ""}` : "—"}
                </div>
                {enCarrito && (
                  <div className="badge bg-accent-light text-accent-dark mt-1.5">
                    {porPeso ? `${enCarrito.pesoGramos} g en carrito` : `${enCarrito.cantidad} en carrito`}
                  </div>
                )}
              </button>
            );
          })}
          {productosFiltrados.length === 0 && (
            <div className="col-span-full text-center text-sm text-textmuted py-12">
              {productos.length === 0 ? "No hay productos con existencia disponible." : "Ningún producto coincide con la búsqueda."}
            </div>
          )}
        </div>
      </div>

      {/* Panel de carrito / cobro */}
      <div className="w-full lg:w-[380px] shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-borderc flex flex-col">
        <div className="p-4 border-b border-borderc shrink-0">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1">Cliente</label>
          <div className="flex gap-2">
            <select className="input-field flex-1" value={clienteId} onChange={(e) => seleccionarCliente(e.target.value ? Number(e.target.value) : "")}>
              <option value="">Consumidor final</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.nit ? ` — ${c.nit}` : ""}</option>)}
            </select>
            <button type="button" className="btn-secondary px-3 shrink-0" onClick={() => setModalCliente(true)} title="Nuevo cliente">
              <Icon name="plus" className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {carrito.length === 0 && (
            <div className="text-center text-xs text-textmuted py-10">
              <Icon name="cart" className="h-8 w-8 mx-auto mb-2 opacity-30" />
              El carrito está vacío. Haga clic en un producto para agregarlo.
            </div>
          )}
          {carrito.map((it) => (
            <div key={it.inventarioPtId} className="rounded-lg border border-borderc p-2.5">
              <div className="flex justify-between items-start gap-2">
                <span className="text-xs font-semibold text-slate-800 leading-tight">{it.producto}</span>
                <button type="button" className="text-red-500 hover:text-red-700 shrink-0" onClick={() => quitarDelCarrito(it.inventarioPtId)}>
                  <Icon name="trash" className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 gap-2">
                {it.unidadVenta === "peso" ? (
                  <div className="flex items-center gap-1">
                    <input
                      className="input-field w-16 text-right text-xs py-1"
                      type="number" min={0} step="10"
                      value={it.pesoGramos ?? 0}
                      onChange={(e) => cambiarPeso(it.inventarioPtId, Number(e.target.value))}
                    />
                    <span className="text-[10px] text-textmuted">g</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <button type="button" className="h-6 w-6 rounded-md bg-surface hover:bg-borderc flex items-center justify-center" onClick={() => cambiarCantidad(it.inventarioPtId, -1)}>
                      <Icon name="minus" className="h-3 w-3" />
                    </button>
                    <span className="text-xs font-semibold w-6 text-center">{it.cantidad}</span>
                    <button type="button" className="h-6 w-6 rounded-md bg-surface hover:bg-borderc flex items-center justify-center" onClick={() => cambiarCantidad(it.inventarioPtId, 1)} disabled={it.cantidad >= it.cantidadDisponible}>
                      <Icon name="plus" className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <input
                  className="input-field w-20 text-right text-xs py-1"
                  type="number" min={0} step="100"
                  value={it.precio}
                  onChange={(e) => cambiarPrecio(it.inventarioPtId, Number(e.target.value))}
                  title={it.unidadVenta === "peso" ? "Precio por kilogramo" : "Precio por unidad"}
                />
                <span className="text-xs font-bold text-slate-800 w-16 text-right">{money(it.cantidad * it.precio)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-borderc space-y-3 shrink-0">
          <div className="flex justify-between text-sm font-semibold text-slate-800">
            <span>Subtotal</span><span>{money(subtotal)}</span>
          </div>
          <p className="text-[10px] text-textmuted -mt-2">El IVA y las retenciones (si el cliente aplica) se calculan al cobrar.</p>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Medio de pago</label>
            <div className="grid grid-cols-3 gap-1.5">
              {(["efectivo", "tarjeta", "transferencia"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMedioPago(m)}
                  className={`rounded-lg border py-1.5 text-[11px] font-semibold transition-colors ${medioPago === m ? "border-accent bg-accent-light text-accent-dark" : "border-borderc text-slate-600 hover:border-slate-300"}`}
                >
                  {MEDIO_PAGO_LABEL[m]}
                </button>
              ))}
            </div>
          </div>

          {medioPago === "efectivo" && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Efectivo recibido (opcional)</label>
              <input
                className="input-field"
                type="number" min={0} step="1000"
                value={efectivoRecibido}
                onChange={(e) => setEfectivoRecibido(e.target.value ? Number(e.target.value) : "")}
                placeholder="Se calcula el cambio al cobrar"
              />
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
              <Icon name="mail" className="h-3.5 w-3.5" /> Enviar comprobante por correo (opcional)
            </label>
            <input
              className="input-field"
              type="email"
              value={correoEnvio}
              onChange={(e) => setCorreoEnvio(e.target.value)}
              placeholder="correo@cliente.com"
            />
            <p className="text-[10px] text-textmuted mt-1">
              El documento equivalente se envía automáticamente a este correo al cobrar (normativa DIAN). Déjelo
              vacío si el cliente no quiere recibirlo por correo.
            </p>
          </div>

          {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}

          <button type="button" className="btn-primary w-full py-3 text-sm" onClick={cobrar} disabled={cobrando || carrito.length === 0}>
            {cobrando ? "Cobrando..." : `Cobrar ${carrito.length > 0 ? money(subtotal) : ""}`}
          </button>
        </div>
      </div>

      {modalCliente && (
        <ModalNuevoCliente
          onClose={() => setModalCliente(false)}
          onCreado={(c) => { setClientes((prev) => [...prev, c]); setClienteId(c.id); setModalCliente(false); }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------- alta rápida de cliente
function ModalNuevoCliente({ onClose, onCreado }: { onClose: () => void; onCreado: (c: ClienteRapido) => void }) {
  const [nombre, setNombre] = useState("");
  const [nit, setNit] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const cliente = await api.post("/pos/clientes", { nombre, nit, telefono, email });
      onCreado({ id: cliente.id, nombre: cliente.nombre, nit: cliente.nit, email: cliente.email });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el cliente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Nuevo cliente</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-textmuted -mt-2">Alta rápida para identificar al comprador de esta venta. Los demás datos (régimen, lista de precios, etc.) se completan luego desde Comercial si hace falta.</p>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre *</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required autoFocus /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">NIT / Cédula</label>
          <input className="input-field" value={nit} onChange={(e) => setNit(e.target.value)} /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Teléfono</label>
          <input className="input-field" value={telefono} onChange={(e) => setTelefono(e.target.value)} /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Correo (para enviarle el comprobante)</label>
          <input className="input-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@cliente.com" /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear y seleccionar"}</button>
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------- recibo / confirmación
function PantallaRecibo({
  recibo, efectivoRecibido, onNuevaVenta, empresaNombre, vendedorNombre, cajaNombre,
}: {
  recibo: Recibo; efectivoRecibido: number | ""; onNuevaVenta: () => void;
  empresaNombre: string; vendedorNombre: string; cajaNombre: string;
}) {
  const cambio = recibo.medioPago === "efectivo" && efectivoRecibido !== "" ? Math.round((Number(efectivoRecibido) - recibo.netoRecibido) * 100) / 100 : null;
  const [estadoCorreo, setEstadoCorreo] = useState(recibo.correoEstado);
  const [destinoCorreo, setDestinoCorreo] = useState(recibo.correoDestino || "");
  const [reenviando, setReenviando] = useState(false);
  const [errorCorreo, setErrorCorreo] = useState<string | null>(null);

  const reenviarCorreo = async () => {
    if (!destinoCorreo.trim()) return;
    setReenviando(true);
    setErrorCorreo(null);
    try {
      const r = await api.post(`/pos/ventas/${recibo.id}/enviar-documento-por-correo`, { correo: destinoCorreo });
      setEstadoCorreo(r.correoEstado);
      if (r.correoEstado !== "enviado") setErrorCorreo(descripcionErrorCorreo(r.correoEstado));
    } catch (e) {
      setErrorCorreo(e instanceof ApiError ? e.message : "No se pudo enviar el correo.");
    } finally {
      setReenviando(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-4 overflow-y-auto">
      <div className="card w-full max-w-sm p-6">
        <div className="text-center mb-4">
          <div className="h-12 w-12 rounded-full bg-accent-light text-accent-dark flex items-center justify-center mx-auto mb-3">
            <Icon name="check" className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold text-slate-800">Venta registrada</h3>
          <p className="text-xs text-textmuted mt-0.5">{recibo.numero} · {recibo.clienteNombre}</p>
        </div>

        <div className="rounded-lg bg-surface p-3 space-y-1.5 mb-4">
          {recibo.items.map((it, i) => (
            <div key={i} className="flex justify-between text-xs text-slate-700">
              <span>{descripcionCantidadItem(it)} × {it.producto}</span>
              <span className="font-medium">{money(it.cantidad * it.precio)}</span>
            </div>
          ))}
        </div>

        <div className="space-y-1.5 mb-4 text-sm">
          <div className="flex justify-between text-textmuted"><span>Base</span><span>{money(recibo.base)}</span></div>
          <div className="flex justify-between font-bold text-slate-800 text-base"><span>Total</span><span>{money(recibo.valorTotal)}</span></div>
          {recibo.netoRecibido !== recibo.valorTotal && (
            <div className="flex justify-between text-textmuted"><span>Neto a cobrar (con retenciones)</span><span>{money(recibo.netoRecibido)}</span></div>
          )}
          <div className="flex justify-between text-textmuted"><span>Medio de pago</span><span>{MEDIO_PAGO_LABEL[recibo.medioPago]}</span></div>
          {cambio !== null && (
            <div className={`flex justify-between font-semibold pt-1.5 mt-1.5 border-t border-borderc ${cambio < 0 ? "text-red-600" : "text-accent-dark"}`}>
              <span>{cambio < 0 ? "Falta por recibir" : "Cambio"}</span><span>{money(Math.abs(cambio))}</span>
            </div>
          )}
        </div>

        {/* Estado del envío del documento equivalente por correo (Resolución DIAN 000165 de 2023) */}
        <div className="rounded-lg border border-borderc p-3 mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Icon name="mail" className="h-4 w-4 text-textmuted shrink-0" />
            <span className="text-xs font-semibold text-slate-700">Envío por correo</span>
            {estadoCorreo === "enviado" && <span className="badge bg-accent-light text-accent-dark ml-auto">Enviado</span>}
            {estadoCorreo === "no_solicitado" && <span className="badge bg-slate-100 text-slate-500 ml-auto">No solicitado</span>}
            {(estadoCorreo === "fallido" || estadoCorreo === "no_configurado") && <span className="badge bg-amber-50 text-amber-700 ml-auto">Pendiente</span>}
          </div>
          {estadoCorreo === "enviado" && (
            <p className="text-[11px] text-textmuted">Comprobante enviado a <strong>{destinoCorreo}</strong>.</p>
          )}
          {estadoCorreo === "no_solicitado" && (
            <p className="text-[11px] text-textmuted">No se indicó un correo para esta venta. Puede enviarlo ahora si el cliente lo pide:</p>
          )}
          {estadoCorreo === "no_configurado" && (
            <p className="text-[11px] text-amber-700">
              El servidor no tiene configurado el envío de correo (SIGEPRO_SMTP_HOST). La venta y el documento
              equivalente ya quedaron registrados con normalidad — descárguelo abajo mientras tanto.
            </p>
          )}
          {estadoCorreo === "fallido" && (
            <p className="text-[11px] text-red-600">No se pudo enviar el correo. Puede reintentar:</p>
          )}
          {errorCorreo && <p className="text-[11px] text-red-600 mt-1">{errorCorreo}</p>}
          {estadoCorreo !== "enviado" && (
            <div className="flex gap-1.5 mt-2">
              <input
                className="input-field text-xs py-1.5 flex-1"
                type="email"
                value={destinoCorreo}
                onChange={(e) => setDestinoCorreo(e.target.value)}
                placeholder="correo@cliente.com"
              />
              <button type="button" className="btn-secondary px-3 text-xs shrink-0" onClick={reenviarCorreo} disabled={reenviando || !destinoCorreo.trim()}>
                {reenviando ? "Enviando..." : "Enviar"}
              </button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <button
            type="button"
            className="btn-secondary w-full flex items-center justify-center gap-2"
            onClick={() => descargarArchivo(`/pos/ventas/${recibo.id}/documento-equivalente`, `documento_equivalente_${recibo.numero}.xml`)}
          >
            <Icon name="fileText" className="h-4 w-4" /> Descargar documento equivalente
          </button>
          <button
            type="button"
            className="btn-secondary w-full flex items-center justify-center gap-2"
            onClick={() => window.print()}
          >
            <Icon name="printer" className="h-4 w-4" /> Imprimir tirilla
          </button>
          <button type="button" className="btn-primary w-full" onClick={onNuevaVenta}>Nueva venta</button>
        </div>
      </div>

      {/* Tirilla imprimible (80mm) — oculta en pantalla, solo visible al imprimir (ver @media print en styles.css) */}
      <div className="hidden print:block tirilla-imprimir font-mono text-[11px] leading-snug p-2">
        <div className="text-center mb-2">
          <div className="font-bold text-sm">{empresaNombre}</div>
          <div>Documento equivalente de venta POS</div>
          <div>(simulado — no timbrado DIAN)</div>
        </div>
        <div className="border-t border-dashed border-black my-1.5" />
        <div>No. {recibo.numero}</div>
        <div>Cliente: {recibo.clienteNombre}</div>
        <div>Cajero: {vendedorNombre}</div>
        <div>Caja: {cajaNombre}</div>
        <div className="border-t border-dashed border-black my-1.5" />
        {recibo.items.map((it, i) => (
          <div key={i} className="flex justify-between">
            <span>{descripcionCantidadItem(it)} x {it.producto}</span>
            <span>{money(it.cantidad * it.precio)}</span>
          </div>
        ))}
        <div className="border-t border-dashed border-black my-1.5" />
        <div className="flex justify-between"><span>Base</span><span>{money(recibo.base)}</span></div>
        <div className="flex justify-between font-bold"><span>TOTAL</span><span>{money(recibo.valorTotal)}</span></div>
        <div className="flex justify-between"><span>Medio de pago</span><span>{MEDIO_PAGO_LABEL[recibo.medioPago]}</span></div>
        {cambio !== null && cambio >= 0 && <div className="flex justify-between"><span>Recibido</span><span>{money(Number(efectivoRecibido))}</span></div>}
        {cambio !== null && cambio >= 0 && <div className="flex justify-between font-bold"><span>Cambio</span><span>{money(cambio)}</span></div>}
        <div className="border-t border-dashed border-black my-1.5" />
        <div className="break-all text-[9px]">CUDE: {recibo.cude}</div>
        <div className="text-center mt-2">¡Gracias por su compra!</div>
      </div>
    </div>
  );
}

// Un ítem "por peso" se guardó como kilogramos (misma convención de siempre
// en inventario), pero se le muestra al cajero/cliente en gramos — la unidad
// en la que realmente se pesó y vendió — tanto en la pantalla de recibo como
// en la tirilla impresa.
function descripcionCantidadItem(it: { cantidad: number; unidadVenta?: "unidad" | "peso"; pesoGramos?: number }) {
  if (it.unidadVenta === "peso") {
    return `${it.pesoGramos ?? Math.round(it.cantidad * 1000)} g`;
  }
  return String(it.cantidad);
}

function descripcionErrorCorreo(estado: string) {
  if (estado === "no_configurado") return "El servidor no tiene configurado el envío de correo (SIGEPRO_SMTP_HOST).";
  if (estado === "fallido") return "No se pudo enviar el correo — verifique la dirección e intente de nuevo.";
  return null;
}
